import { Flex } from '@utilitywarehouse/hearth-react';
import { Formik, useFormikContext } from 'formik';
import { useCallback, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { NavBar } from '@components/NavBar';
import { ScrollToError } from '@components/ScrollToError';
import { paths } from '@constants/paths';
import { FormSync } from '@features/Remote/Interaction/FormSync';
import { MobileTariffType } from '@generated/gql/graphql';

import { MobileFAQ } from '../MobileFAQ';
import { OfferBanner } from '../OfferBanner';
import { useMobileContext } from '../Provider/Context';
import { orderedFormFields } from '../constants';
import { useMobileTracking } from '../hooks/useMobileTracking';
import { SimSettingsFormValues } from '../types';
import { ChooseSimFormSection } from './ChooseSimFormSection';
import { SimSettingsFormSection } from './SimSettingsFormSection';
import { validateForm } from './validateForm';

const SimFormInternal = () => {
  const { values, setValues, handleSubmit } =
    useFormikContext<SimSettingsFormValues>();
  const { simInitialValues, mobileLoading, mobileTariffs, formRef } =
    useMobileContext();

  const showUnlimitedMaxPromoLegal = mobileTariffs.some(
    (t) =>
      t.tariffType === MobileTariffType.GoUnlimited &&
      (t.discounts?.length ?? 0) > 0
  );

  // restore the saved form values, returned as `simInitialValues`, and
  // populate them in formik state. Must be done async since loading the page
  // will set the active sim ID after formik's `initialValues` are already set.
  // A quirk of the initial mobile implementation, which ideally should be a
  // "pull" approach to fetch values as formik loads, but there was no time
  // to implement as of now.
  useEffect(() => {
    // only do this if there is no set formik value, otherwise
    // we can't actually change the form ourselves
    if (simInitialValues.simPlanId && !values.simPlanId) {
      setValues({
        ...values,
        ...simInitialValues,
      });
    }
  }, [simInitialValues, values, setValues]);

  if (!mobileLoading && mobileTariffs.length === 0) {
    throw new Error('Missing available mobile tariffs');
  }

  return (
    <>
      <Flex
        asChild
        direction="column"
        spacing="2xl"
        paddingBottom={{
          desktop: '600',
          tablet: '500',
          mobile: '400',
        }}
      >
        <form
          onSubmit={handleSubmit}
          ref={formRef}
          className="simForm__wrapper"
        >
          <ChooseSimFormSection
            mobileTariffs={mobileTariffs}
            showUnlimitedMaxPromoLegal={showUnlimitedMaxPromoLegal}
            loading={mobileLoading}
          />
          <SimSettingsFormSection />
        </form>
      </Flex>
      <FormSync />
      <ScrollToError orderedFields={orderedFormFields} />
    </>
  );
};

const SimForm = () => {
  const navigate = useNavigate();
  const { simId } = useParams();
  const {
    mobileLoading,
    selectedSims,
    formRef,
    submitSim,
    simInitialValues,
    editSim,
  } = useMobileContext();

  useEffect(() => {
    if (simId) {
      editSim(simId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [simId]);

  const { trackSimSubmitted } = useMobileTracking(selectedSims);

  const handleSubmitSimSettings = useCallback(
    (values: SimSettingsFormValues) => {
      trackSimSubmitted(values);
      submitSim(values);
      navigate(paths.mobile);
    },
    [navigate, submitSim, trackSimSubmitted]
  );

  return (
    <>
      <Flex direction="column" className="wrapper" spacing="2xl">
        <OfferBanner />
        <Formik
          initialValues={simInitialValues}
          onSubmit={handleSubmitSimSettings}
          validate={validateForm}
          validateOnChange={false}
          validateOnBlur={false}
        >
          <SimFormInternal />
        </Formik>
      </Flex>
      <MobileFAQ />
      <NavBar
        nextLabel={simId ? 'Save SIM' : 'Add SIM'}
        onNext={() => formRef?.current?.requestSubmit()}
        nextDisabled={mobileLoading}
      />
    </>
  );
};

export { SimForm };
